// **Slide copy and diagram content**, transcribed from `docs/PRESENTATION.md`'s own words and
// mermaid sketches (`demo-presentation-shell` task 4.2). Kept as plain data, separate from the
// components that render it, so a script wording tweak before the show touches one small module
// rather than JSX.
//
// Rewritten 2026-08-09 for the streamlined script: the Korean "you" example is gone, replaced by
// the love/tiramisu example (English's one word for love vs. six Ancient Greek examples), and the
// pointing emoji by a hug. The metaphor family the script's own header names — translation into a
// *more precise* language forces someone to fill the gaps — is what those two swaps buy.

/** Segments marked `**bold**` in the script are kept as markers here and split at render
 * (`QuoteSlide`), so the emphasis lives with the copy rather than in JSX. */
export const KARPATHY_QUOTE = [
  `There's a new kind of coding I call "vibe coding", where you fully give in to the vibes, embrace exponentials, and forget that the code even exists…`,
  `I'm building a project or webapp, but it's not really coding — I just **see** stuff, **say** stuff, **run** stuff, and copy paste stuff, and it **mostly works**.`,
];
export const KARPATHY_ATTRIBUTION = 'Andrej Karpathy, 3/2/25';

// --- Apiophobia (docs/PRESENTATION.md, "On language") --------------------------------------

export const APIOPHOBIA_WORD = 'Apiophobia';
export const APIOPHOBIA_ORIGINS = [
  { fragment: 'api-', origin: 'Latin', meaning: 'bee' },
  { fragment: '-phobia', origin: 'Ancient Greek', meaning: 'fear' },
];
/** What the two fragments add up to — the slide shows the word itself beside this, so this is the
 * *gloss*, in the same shape as the two origins' own. */
export const APIOPHOBIA_RESULT = 'English for \u201Cfear of bees\u201D';
/** Three reveals, and the order is **`-phobia`, then `api- +`, then `= Apiophobia`** — the order the
 * presenter says the sentence in, not the order the equation reads left to right (user story 4;
 * confirmed deliberate, not a transcription slip). The equation's *layout* is unchanged, so every
 * slot is reserved from step 0 and nothing moves as the reveals land (user story 3/7). */
export const APIOPHOBIA_MAX_REVEAL = 3;

// --- Translation/vibes chart ---------------------------------------------------------------
// The script's three successive ```mermaid blocks under "On language", as two slides: the chart
// is shown, the love example interrupts it, and then "the translation chart returns, now with
// German added". So `TranslationChartSlide`'s steps are
//   0 — my brain -> English -> Ancient Greek -> your brain
//   1 — the Translation edge splits into hard (Greek) / easy (German) branches
//   2 — a third branch: my brain -> a hug -> your brain
// and the *return* slide (`slide-translation-chart-german`) simply mounts the same component at a
// base step of 1, which is why there is one component and not two.
//
// 2026-08-11: the hugging-face emoji is back to the text **"A hug"** the script's own mermaid names
// (a glyph in a projected diagram read as decoration, and it renders differently on every machine),
// and all three of the chart's outgoing "meaning" edges — "Meaning kept", "Meaning survives",
// "Understood" — collapse to one word, **"Vibes"**: the presenter's point is that the three are the
// same thing, which three different labels contradicted (user story 9).
export const TRANSLATION_CHART_MAX_REVEAL = 2;

// --- The love example (docs/PRESENTATION.md, "On language") ---------------------------------

export const LOVE_SENTENCES = ['I love my family.', 'I love tiramisu.'] as const;

/** Six Ancient Greek examples for love, in the script's order. `storgē` is the one that attaches
 * itself to "I love my family". */
export const GREEK_LOVES = [
  { word: 'érōs', gloss: 'passion' },
  { word: 'philía', gloss: 'friendship' },
  { word: 'storgē', gloss: 'family' },
  { word: 'agápē', gloss: 'selfless devotion' },
  { word: 'xenía', gloss: 'hospitality' },
  { word: 'philautía', gloss: 'self-love' },
] as const;
export const GREEK_LOVE_FOR_FAMILY = 'storgē';

/** "A Greek would reach for a different word entirely: hēdonē… it's the root of 'hedonist'." */
export const HEDONE = { word: 'hēdonē', gloss: 'pleasure' } as const;
/** Three reveals since 2026-08-11 (user stories 5/6). Both English sentences are now on screen when
 * the slide *arrives* — they used to be step 0's own content in a slide that then looked broken
 * while the presenter set the example up — and the two Greek words arrive on separate clicks under
 * the sentence each belongs to rather than as a list:
 *   step 1 — the English/Greek split: one English word for love on the left, six Greek examples on the
 *            right, **no** emphasis on storgē yet.
 *   step 2 — storgē under "I love my family", and the same word marked in the Greek column.
 *   step 3 — hēdonē under "I love tiramisu."
 * `LOVE_NONE_OF_THESE` ("none of these", a caption under the tiramisu at the old step 1) is gone:
 * the split now makes that point structurally, and a caption that appears and then disappears is
 * exactly the movement user story 7 rules out. */
export const LOVE_MAX_REVEAL = 3;

// --- Programming-as-translation chart: base + two script mermaid diagrams ------------------
// step 0: my_brain -> English -> Code -> Computer (base).
// step 1 (reveal-language-range): Code is replaced by the four named languages. Their counterparts
//         from the love chart are the presenter's line, not node sublabels (2026-08-13).
// step 2 (reveal-llm): an LLM node is inserted between English and the four languages.
export const PROGRAMMING_LANGUAGES = [
  {
    id: 'cpp',
    label: 'C++',
    inLabel: 'Difficult translation',
    outLabel: 'Fast, powerful execution',
  },
  {
    id: 'python',
    label: 'Python',
    inLabel: 'Easy translation',
    outLabel: 'Slow, powerful execution',
  },
  {
    id: 'excel',
    label: 'Excel',
    inLabel: 'Little translation',
    outLabel: 'Fast, limited execution',
  },
  {
    id: 'bpp',
    label: 'B++',
    inLabel: 'Unusual translation',
    outLabel: 'Fast, weird execution',
  },
] as const;
export const PROGRAMMING_CHART_MAX_REVEAL = 2;

// --- Wrap-up copy ----------------------------------------------------------------------------

// **The hand-off slide** (2026-08-13). "See stuff, say stuff, run stuff." and the separate "Your
// turn!" hand-off card (the old `nav-rounds-handoff` nav target) are now one slide at the end of the
// match section: the framing line and the invitation were the same beat said twice, and the room
// only needs the address once, at the point they can act on it.
/** The newline is the slide's line break, rendered by `white-space: pre-line` — the heading reads as
 * two balanced lines rather than wherever 1920px happens to wrap it. It collapses to a space in the
 * accessible name, so the heading is still one sentence to a screen reader. */
export const SEE_SAY_RUN_LINE = "It's your turn to see stuff,\nsay stuff and run stuff";
/** Where the audience plays. The QR on the hand-off slide encodes exactly this. */
export const PUBLIC_PLAY_URL = 'https://waggle.northrop.com.au';
/** The same address as the room reads it — no scheme, because a projected `https://` is noise. */
export const PUBLIC_PLAY_LABEL = 'waggle.northrop.com.au';

export const CLOSING_TITLE = 'Vibe coding at Northrop';

export const SUNSET_TAGLINE = 'One language for bees. One for engineering.';

/** Two reveals (user story 10): a **Waggle** box under B++, then an **Octopus** box under Sunset —
 * one beat each, so the payoff lands twice rather than once. Both slots are reserved from step 0. */
export const SUNSET_MAX_REVEAL = 2;

// --- The finisher (`slide-finisher`) ---------------------------------------------------------

/** The two lines the talk ends on (user story 11). **Three reveals since 2026-08-13**: the slide
 * arrives as the empty circle, each line lands on its own click, and the Festival mark covers both
 * on the last one. The lines used to be on screen from step 0, which spent the ending in one beat;
 * the circle alone is the pause the presenter speaks into. Both stay up once they arrive — nothing
 * replaces anything until the mark. No wordmark: the closing slide before it carries that. */
export const FINISHER_LINES = ['Express your intent.', 'Make your impact.'] as const;
export const FINISHER_MAX_REVEAL = 3;

export const STARTER_PACK_ITEMS = [
  'How to access and use vibe-coding tools',
  'Example prompts',
  'App ideas',
  'Storing data safely',
  'Distributing apps to others',
] as const;
export const STARTER_PACK_TITLE = 'digital.northrop.com.au/vibe-coding';
export const STARTER_PACK_SUBTITLE = 'Your one-stop shop for vibe-coding.';
